import './styles/trustarc.css'
import { useCallback, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import AppRouter from './Router';
import { Provider } from 'react-redux';
import { store } from './store';
import { Modal } from './shared/ui';

import { useAppSelector, useAppDispatch } from '@/shared/hooks/store';
import { selectAuthState } from '@/store/slices/auth/selectors';
import { useCookies } from 'react-cookie';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import { userDetails } from '@/store/slices/onboarding/selectors';
import { resetPersistSlice } from './store/helpers';
import { selectMicEvents, selectMicModalOpen } from '@/store/slices/v-t-s/selectors';
import { clearVoiceToSpeechTracking, closeModal } from '@/store/slices/v-t-s';
import { usePostHog } from '@/shared/hooks/usePostHog'
import { SessionRecordingPrompt } from '@/shared/components/session-recording-prompt';
import { ChatWidgetWrapper } from '@/shared/components/chat-widget';
import { useLogoutOnInactivity } from './shared/hooks/useResetStore';

const App = () => {
    return (
        <Provider store={store}>
            <RouterComponent />
        </Provider>
    );
};

export default App;
const RouterComponent = () => {
    const [cookies, setCookie, removeCookie] = useCookies(['sessionRecording']);
    const [showSessionRecordingPrompt, setShowSessionRecordingPrompt] = useState(false);
    const userDetailsData = useAppSelector(userDetails);
    const authState = useAppSelector(selectAuthState);
    const authorizedId = authState.id;
    const token = authState.token;
    const sendDataToGTM = useGTMDispatch();
    const dispatch = useAppDispatch();
    const micEvents = useAppSelector(selectMicEvents);
    const micModalOpen = useAppSelector(selectMicModalOpen);
    const posthog = usePostHog();

    useLogoutOnInactivity();

    useEffect(() => {
        if (!token) {
            window.sessionStorage.removeItem('pID');
        }
    }, [])

    useEffect(() => {
        const isAuthorized = !!authorizedId
        const userId = authorizedId?.toString() || '';

        if (!isAuthorized) {
            posthog?.stopSessionRecording()
            removeCookie('sessionRecording');
            setShowSessionRecordingPrompt(false);

        } else if (userDetailsData?.email) {
            const sessionRecording = cookies.sessionRecording;
            const currentPid = window.sessionStorage.getItem('pID'); // post hog id collected from incoming redirect decoded from code
            if (currentPid) {
                posthog?.alias(currentPid, userId);
            }
            posthog?.identify(userId.toString(), { email: userDetailsData?.email });
            sendDataToGTM({ event: GtmEvent.RETURN, userId, sessionRecording: !!cookies.sessionRecording });
            if (sessionRecording !== undefined) {
                if (sessionRecording) {
                    posthog?.startSessionRecording();
                } else if (!sessionStorage.getItem('sessionRecordingPromptShown')) {
                    setShowSessionRecordingPrompt(true);
                }
            }
        }
    }, [authorizedId, cookies.sessionRecording, posthog, removeCookie, sendDataToGTM, userDetailsData?.email]);

    useEffect(() => {
        return () => {
            posthog?.stopSessionRecording();
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = () => {
            sessionStorage.removeItem("redirectAfterLogin");
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [])

    // if we come to the app with a query param email, we need to reset the auth slice if the email is different
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const email = queryParams.get('email')?.trim().toLowerCase() || '';
        if (email && userDetailsData?.email !== email) {
            resetPersistSlice('auth');
        }
    }, [userDetailsData?.email]);

    const handleAcceptRecording = useCallback(() => {
        setCookie('sessionRecording', 'true', { path: '/' });
        posthog?.startSessionRecording();
        sessionStorage.setItem('sessionRecordingPromptShown', 'true');
        setShowSessionRecordingPrompt(false);
    }, [setCookie, posthog]);

    const handleDeclineRecording = useCallback(() => {
        sessionStorage.setItem('sessionRecordingPromptShown', 'true');
        setShowSessionRecordingPrompt(false);
    }, []);

    return (
        <ChatWidgetWrapper>
            <div>
                <Modal
                    onClose={() => {
                        dispatch(closeModal());
                    }}
                    open={micModalOpen}
                    dataTestId="clear-mic"
                >
                    <button
                        className="mb-5 bg-primary p-3"
                        onClick={() => {
                            dispatch(clearVoiceToSpeechTracking());
                        }}
                        data-testid="button-clear-mic-events"
                    >
                        Clear mic events
                    </button>
                    <div className="flex flex-col gap-4">
                        {micEvents.map((event, index) => (
                            <div key={index}>{event}</div>
                        ))}
                    </div>
                </Modal>
                <RouterProvider router={AppRouter} />
                {showSessionRecordingPrompt && (
                    <SessionRecordingPrompt
                        onAccept={handleAcceptRecording}
                        onDecline={handleDeclineRecording}
                    />
                )}
            </div>
        </ChatWidgetWrapper>
    );
};
